import { useCallback, useRef, useState } from 'react';
import type { TicketMessage, TicketMessagesPagination } from '@/types/ticket';

type FetchTicketMessages = (
  ticketId: string,
  params?: { beforeMessageId?: string }
) => Promise<{ messages: TicketMessage[]; pagination: TicketMessagesPagination }>;

export function useTicketMessagePagination(fetchMessages: FetchTicketMessages) {
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [pagination, setPagination] = useState<TicketMessagesPagination | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);

  const resetMessages = useCallback((next: TicketMessage[], pag: TicketMessagesPagination) => {
    setMessages(next);
    setPagination(pag);
  }, []);

  const prependOlderMessages = useCallback(
    async (ticketId: string): Promise<boolean> => {
      if (!pagination?.hasMoreOlder || loadingOlderRef.current) return false;
      const oldest = messages[0];
      if (!oldest) return false;

      loadingOlderRef.current = true;
      setLoadingOlder(true);
      try {
        const { messages: older, pagination: nextPag } = await fetchMessages(ticketId, {
          beforeMessageId: oldest.messageId,
        });
        if (!older.length) {
          setPagination((prev) =>
            prev ? { ...prev, hasMoreOlder: nextPag.hasMoreOlder } : nextPag
          );
          return false;
        }
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.messageId));
          const fresh = older.filter((m) => !ids.has(m.messageId));
          return [...fresh, ...prev];
        });
        setPagination((prev) => ({
          ...nextPag,
          totalCount: prev?.totalCount ?? nextPag.totalCount,
        }));
        return true;
      } finally {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    },
    [fetchMessages, messages, pagination?.hasMoreOlder]
  );

  const appendMessage = useCallback((msg: TicketMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.messageId === msg.messageId)) return prev;
      return [...prev, msg];
    });
  }, []);

  const patchMessage = useCallback((messageId: string, patch: Partial<TicketMessage>) => {
    setMessages((prev) =>
      prev.map((m) => (m.messageId === messageId ? { ...m, ...patch } : m))
    );
  }, []);

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
  }, []);

  return {
    messages,
    setMessages,
    pagination,
    loadingOlder,
    resetMessages,
    prependOlderMessages,
    appendMessage,
    patchMessage,
    removeMessage,
  };
}
